import { get } from 'svelte/store';
import { readDeviceStatus, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
import { toast } from '@Shelly/shelly-design-system';
//Config
import { devices_config } from '@devices/index';
import { devices_config_wave } from 'src/devices_config_wave';
//Formatters
import { format } from 'src/services/i18n';
//Stores
import { devices_status, devices_settings } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
import { app, os } from 'src/stores/session/native-bridge';
//Types
import type { Device } from 'src/types/Device';
//Helpers
import { open_external_link } from 'src/services/native_container_requests_ios';
import { open_external_link_android } from 'src/services/native_container_requests_android';
import type { WiFiRequest } from 'src/types/requests/settings/networks/wifi_request';
import { gen2Request } from 'src/services/gen_2_http_reqs';
import { navigateToSetting } from 'src/stores/ui/navigate-to';
import { getBleSignalStrength } from 'src/helpers/virtual_helpers/blutrv_helpers';
import { convertDateFormat } from 'src/helpers/date_helper';
import { getComponentByDevice } from 'src/helpers/virtual_helpers/virtual_components_helpers';
import { isPeripheralDevice } from 'src/helpers/addons/addon_device_helpers';
import { isXT1Module } from 'src/helpers/xt1_helpers/template_helpers';
import {
	get_ip,
	get_ssid,
	is_gen_2,
	is_gen_z,
	isBluTRV,
	isDiffuser,
	isPill,
	isPlusUni,
	macFromId,
	real_id,
} from 'src/helpers/interface_helpers';
import { capFirstChar, formatSnakeCaseToTitle, hex_to_decimal, short_id } from 'src/helpers/utilities';
import { isDeviceTemplatesSupported } from 'src/helpers/settings/settings_config_helpers';

type DeviceInfoEntry = {
	type: string;
	title: string;
	desc?: string;
	isLink?: boolean;
	copyButton?: boolean;
	cloneSettings?: boolean;
	saveConfiguration?: boolean;
	linkClick?: () => void;
	appliedForGenerations?: string[];
};

export const getDeviceInfoEntries = (device: Device, status, settings): DeviceInfoEntry[] => {
	let ip = getIp(status);
	const deviceMac = getDeviceMac(device, settings);

	let deviceInfoEntres: DeviceInfoEntry[] = [
		{
			type: 'deviceId',
			title: get(format)('text_list_device_info_id'),
			desc: real_id(device.id),
			copyButton: true,
		},
		{
			type: 'deviceIdDec',
			title: get(format)('text_list_device_info_id_dec'),
			desc: `${hex_to_decimal(real_id(device.id))}`,
			copyButton: true,
			appliedForGenerations: ['1', '2', '3', '4'],
		},
		{
			type: 'deviceMac',
			title: get(format)('text_list_device_info_mac'),
			desc: deviceMac,
			copyButton: true,
			appliedForGenerations: ['GBLE'],
		},
		{
			type: 'serialNumber',
			title: get(format)('text_serial_number', { default: 'Serial Number' }),
			desc: status?.snNumber || get(format)('data_not_loaded', { default: "Couldn't load data" }),
			appliedForGenerations: ['GXP'],
		},
		{
			type: 'batterySerialNumber',
			title: get(format)('text_battery_pack', {
				default: 'Battery Serial Number',
			}),
			desc:
				status?.packData?.[0]?.sn ||
				get(format)('data_not_loaded', { default: "Couldn't load data" }),
			appliedForGenerations: ['GXP'],
		},
		{
			type: 'deviceIp',
			title: get(format)('text_list_device_info_ip'),
			desc: ip,
			isLink: !getIsDisconnected(getRssi(status)),
			linkClick: () => {
				!getIsDisconnected(getRssi(status)) ? navigate_to_local(ip) : null;
			},
			copyButton: ip === get(format)('data_not_loaded') ? false : true,
			appliedForGenerations: ['1', '2', '3', '4'],
		},
		{
			type: 'deviceType',
			title: get(format)('text_list_device_info_type'),
			desc: getType(device),
			copyButton: true,
		},
		{
			type: 'deviceModel',
			title: get(format)('text_list_device_model', { default: 'Device Model' }),
			desc: getDeviceModel(device),
			copyButton: true,
			appliedForGenerations: ['1', '2', '3', '4', 'GBLE', 'GXP'],
		},
		{ ...getMainDeviceInfo(device) },
		{
			type: 'deviceFirmware',
			title: get(format)('text_firmware_version', {
				default: 'Firmware Version',
			}),
			desc:
				device.gen === 'GBLE'
					? device?.fwID ||
						devices_status.getByRealId(device.id)?.['fwversion:0']?.version ||
						get(format)('data_not_loaded', { default: "Couldn't load data" })
					: is_gen_2(device)
						? devices_settings.getByRealId(device.id)?.sys?.device.fw_id ||
							devices_settings.getByRealId(device.id)?.DeviceInfo?.fw_id
						: settings?.fw,
			isLink: false, //!device.shared && settings && status && status?.online,
			// Always enable the link regardless of device state
			linkClick: () => {
				// For virtual and peripheral devices, use the parent device ID for navigation
				const deviceId = device.channel >= 100 ? real_id(device.id).split('_')[0] : device.id;

				if (get(devices)[deviceId]) {
					navigateToSetting(deviceId, 'settings', 'firmware_version');
				}
			},
			appliedForGenerations: ['1', '2', '3', '4', 'GBLE'],
		},
		{
			type: 'deviceChannel',
			title: get(format)('text_list_device_info_channel'),
			desc: `${device?.channel}`,
			appliedForGenerations: ['1', '2', '3', '4'],
		},
		{
			type: 'deviceNetwork',
			title: get(format)('text_list_device_info_network'),
			desc: getSsid(device, status, settings),
			appliedForGenerations: ['1', '2', '3', '4'],
		},
		{
			type: 'deviceSignal',
			title: get(format)('text_list_device_info_signal'),
			desc: capFirstChar(
				isBluTRV(device.type)
					? getBleSignalStrength(getRssi(status))
					: getSignalLevel(getRssi(status)),
			),
			appliedForGenerations: ['1', '2', '3', '4'],
		},
		{
			type: 'deviceLastSeen',
			title: get(format)('text_list_device_info_last_seen', {
				default: 'Last seen:',
			}),
			desc: !getLastSeenUpdate(device, status, settings)
				? get(format)('data_not_loaded', { default: "Couldn't load data" })
				: getLastSeenUpdate(device, status, settings),
			appliedForGenerations: ['1', '2', '3', '4', 'GXP'],
		},
	].filter(
		(d) =>
			(typeof d.appliedForGenerations === 'undefined' ||
				d.appliedForGenerations.indexOf(device.gen + '') > -1 ||
				((device.gen === 'GBLE' ||
					device.type === 'SBTR-001AEU' ||
					device.type === 'bthome-device') &&
					d.type == 'deviceMac') ||
				(device?.jti && d.type == 'deviceMac')) &&
			!((device?.channel < 100 || device.shared) && d.type == 'parentDevice') &&
			!(device?.channel >= 100 && d.type == 'deviceChannel') &&
			!(device?.jti && d.type == 'deviceIdDec'),
	);

	return deviceInfoEntres;
};

const getDeviceMac = (device: Device, settings): string => {
	if (device?.addr) return device.addr;
	const component = device?.type === 'bthome-device' ? getComponentByDevice(device) : null;
	if (component && settings?.[component]?.addr) {
		return settings[component].addr;
	}

	const mac = device?.mac || macFromId(device.id) || '';
	return (
		mac
			.toLowerCase()
			.match(/.{1,2}/g)
			?.join(':') || ''
	);
};

export const getDeviceModel = (device: Device) => {
	const allDevices = get(devices);
	const deviceId = real_id(device.id);
	return /^(virtual_device)$/.test(device.category) || isPeripheralDevice(device?.type)
		? allDevices[deviceId]?.type ||
				devices_settings.getByRealId(deviceId)?.DeviceInfo?.model ||
				device.type
		: device.type;
};

export const getMainDeviceInfo = (device: Device) => {
	const allDevices = get(devices);
	const deviceId = real_id(device.id);
	const $format = get(format);
	const deviceType = allDevices[deviceId]?.type;
	const subSetting = isPeripheralDevice(device.type)
		? isPlusUni(deviceType) || isPill(deviceType)
			? 'peripherals'
			: 'addon_sensors'
		: device.category == 'virtual_device'
			? 'virtual_components'
			: 'main_controls';
	return {
		type: 'parentDevice',
		title: $format('text_list_main_device', { default: 'Main Device' }),
		desc: allDevices[deviceId]?.name,
		isLink:
			allDevices[deviceId] !== undefined && !device?.shared && readDeviceStatus(deviceId)?.online,
		linkClick: () => {
			if (allDevices[deviceId]) {
				navigateToSetting(deviceId, subSetting);
			}
		},
		appliedForGenerations: ['2', '3', '4'],
	};
};

const getLastSeenUpdate = (device, status, settings) => {
	let lastSeenUpdate = null;

	if (isBluTRV(device.type)) {
		const component = getComponentByDevice(device);
		const deviceStatus = devices_status.getByRealId(device.id);
		lastSeenUpdate = deviceStatus?.[component]?.last_updated_ts;
		if (lastSeenUpdate) {
			lastSeenUpdate = convertDateFormat(Number(lastSeenUpdate) * 1000);
		}
	} else {
		lastSeenUpdate =
			status?._updated || settings?._updated
				? convertDateFormat(status?._updated * 1000 || settings?._updated)
				: null;
	}

	return lastSeenUpdate;
};

const getSignalLevel = (rssi) => {
	switch (true) {
		case rssi === null || rssi === undefined:
			return get(format)('disconnected', { default: 'Disconnected' });
		case rssi < -80:
			return get(format)('signal_val_un', { default: 'Unusable' });
		case rssi < -70:
			return get(format)('signal_val_w', { default: 'Very Poor' });
		case rssi < -67:
			return get(format)('signal_val_p', { default: 'Poor' });
		case rssi < -30:
			return get(format)('signal_val_g', { default: 'Good' });
		case rssi >= -30:
			return get(format)('signal_val_vg', { default: 'Very Good' });

		default:
			return get(format)('disconnected', { default: 'Disconnected' });
	}
};

const getSsid = (device, status, settings) => {
	return (
		get_ssid(settings, status, device) ||
		get(format)('data_not_loaded', { default: "Couldn't load data" })
	);
};

const getRssi = (status) => {
	return typeof status?.wifi_sta?.rssi !== 'undefined' ? status?.wifi_sta?.rssi : status?.wifi?.rssi;
};

const getType = (device: Device) => {
	if (isXT1Module(device.type) && devices_config[device.type]?.modes?.[device.service_type]) {
		return devices_config[device.type].modes[device.service_type].name;
	}
	let deviceType = formatSnakeCaseToTitle(
		is_gen_z(device)
			? devices_config_wave[device.type]?.friendly_name || device.type
			: devices_config[device.type]?.friendly_name || device.type,
	);
	return isDiffuser(device.type) ? deviceType : `Shelly ${deviceType}`;
};

const getIsDisconnected = (rssi: number) => {
	return typeof rssi === 'undefined';
};

const getIp = (status) => {
	return status?.eth?.ip
		? status.eth.ip
		: typeof status?.wifi_sta !== 'undefined'
			? status?.wifi_sta?.ip
			: status?.wifi?.sta_ip ||
				get(format)('data_not_loaded', {
					default: "Couldn't load data",
				});
};

export const getHostIp = async (ssid: string) => {
	const host_id = ssid.split('-')[1].toLowerCase(); //getting host id
	const small = short_id(host_id);
	let ip: string = '';
	if (typeof get(devices)[host_id] !== 'undefined' || typeof get(devices)[small] !== 'undefined') {
		const id = typeof get(devices)[host_id] !== 'undefined' ? host_id : small;
		const host_ip = get_ip(devices_status.getByRealId(id)); //getting host ip
		const method = 'WiFi.ListApClients';
		const params = {};

		const res: WiFiRequest = await gen2Request(get(devices)[host_id], method, params); //getting list of Wi-Fi extended devices

		if (res.isok) {
			if (typeof res.ap_clients !== 'undefined') {
				res.ap_clients.forEach((client) => {
					if (client.ip === ip) {
						ip = `${host_ip}:${client.mport}`; //changing client ip visualization with host ip and port
					}
				});
			}
			if (typeof res?.data?.ap_clients !== 'undefined') {
				//due to some unknown changes we need to watch for data also
				res?.data?.ap_clients.forEach((client) => {
					if (client.ip === ip) {
						ip = `${host_ip}:${client.mport}`; //changing client ip visualization with host ip and port
					}
				});
			}
		} else {
			toast.open({ type: 'danger', title: get(format)('error_global') });
		}
	} else {
		ip = get(format)('host_device_not_owned');
	}

	return ip;
};

const navigate_to_local = (ip: string) => {
	//if user isn't host owner prevent navigation because not able to get nothing
	if (/\d/gi.test(ip)) {
		if (get(app)) {
			if (get(os) === 'IOS') {
				open_external_link(`http://${ip}`);
			} else {
				open_external_link_android(`http://${ip}`);
			}
		} else {
			window.open(`http://${ip}`);
		}
	} else {
		return;
	}
};
